import React, { useRef, useEffect, memo, useCallback } from 'react'
import { Send, Paperclip, FileText, X, Loader2 } from 'lucide-react'
import type { AttachedFile } from '../types/AttachedFile'
import { useChatStore } from '../../../stores/chatStore'

interface ChatInputProps {
    agentId: string | null
    value: string
    onChange: (value: string) => void
    onSend: () => void
    attachedFiles?: AttachedFile[]
    onRemoveFile?: (fileId: string) => void
    onAttachFile?: () => void
    placeholder?: string
    isSubmitting?: boolean
}

export const ChatInput: React.FC<ChatInputProps> = memo(({
    agentId,
    value,
    onChange,
    onSend,
    attachedFiles = [],
    onRemoveFile,
    onAttachFile,
    placeholder = "Pergunte, crie, pesquise, mencione com @",
    isSubmitting = false
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Phase 4.1: Atomic Lifecycle Selector
    const lifecycle = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.lifecycle || 'idle' : 'idle'
        , [agentId]))

    const isProcessing = lifecycle === 'thinking' || lifecycle === 'streaming' || isSubmitting

    // Force initial height and prevent expansion from layout changes
    useEffect(() => {
        const textarea = textareaRef.current
        if (!textarea) return

        // Force initial height
        textarea.style.height = '36px'

        // Create ResizeObserver to detect parent container changes
        const resizeObserver = new ResizeObserver(() => {
            // Only reset if input is empty
            if (!value.trim()) {
                textarea.style.height = '36px'
            }
        })

        // Observe the chat panel container
        const chatPanel = textarea.closest('.chat-panel-master')
        if (chatPanel) {
            resizeObserver.observe(chatPanel)
        }

        return () => {
            resizeObserver.disconnect()
        }
    }, [value])

    // Auto-resize textarea based on content
    useEffect(() => {
        const textarea = textareaRef.current
        if (!textarea) return

        // Only resize if there's content
        if (value.trim()) {
            // Use requestAnimationFrame to avoid layout thrashing
            requestAnimationFrame(() => {
                textarea.style.height = '36px'
                const newHeight = Math.max(36, Math.min(textarea.scrollHeight, 120))
                textarea.style.height = newHeight + 'px'
            })
        } else {
            // Empty input, force minimum height
            textarea.style.height = '36px'
        }
    }, [value])

    // Handle keyboard shortcuts
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (value.trim() || attachedFiles.length > 0) {
                onSend()
            }
        }
    }

    const canSend = (value.trim() || attachedFiles.length > 0) && !isProcessing

    return (
        <div className="p-3 flex-shrink-0">
            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                    {attachedFiles.map((file) => (
                        <div key={file.id} className="relative group w-14 h-14 flex-shrink-0" title={file.name}>
                            <div className="w-full h-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                {file.type.startsWith('image/') ? (
                                    <>
                                        <img
                                            src={file.data}
                                            alt={file.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none'
                                                const icon = e.currentTarget.nextElementSibling
                                                if (icon) icon.classList.remove('hidden')
                                            }}
                                        />
                                        <FileText className="w-6 h-6 text-gray-500 dark:text-gray-400 hidden" />
                                    </>
                                ) : (
                                    <FileText className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                                )}
                            </div>

                            {/* Remove Button */}
                            {onRemoveFile && (
                                <button
                                    onClick={() => onRemoveFile(file.id)}
                                    className="absolute -top-1.5 -right-1.5 p-0.5 bg-gray-200 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    title="Remover arquivo"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}

                            {/* Upload Progress Overlay */}
                            {file.uploadStatus === 'uploading' && (
                                <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}

                            {/* Error Indicator */}
                            {file.uploadStatus === 'error' && (
                                <div className="absolute inset-0 bg-red-500/20 rounded-lg border border-red-500 flex items-center justify-center">
                                    <X className="w-4 h-4 text-red-500" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Input area with gradient border */}
            <div className="rounded-lg p-[1px] bg-gray-200 dark:bg-gradient-to-br dark:from-blue-400/40 dark:via-purple-500/40 dark:to-pink-400/30 border-0">
                <div className="bg-gray-50 dark:bg-black rounded-lg h-full overflow-hidden">
                    <textarea
                        ref={textareaRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isProcessing ? "O robô está pensando..." : placeholder}
                        disabled={isProcessing}
                        className="w-full px-3 py-1.5 bg-transparent resize-none focus:outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 leading-tight overflow-y-auto"
                        rows={1}
                        style={{ minHeight: '36px', maxHeight: '120px', height: '36px', lineHeight: '1.4', willChange: 'height' }}
                    />
                    <div className="flex items-center justify-between px-3 pb-2 pt-1">
                        <div className="flex items-center gap-2">
                            {/* Attach file button */}
                            {onAttachFile && (
                                <button
                                    onClick={onAttachFile}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-[#1a1a1a] rounded transition-colors"
                                    title="Anexar arquivo"
                                    disabled={isProcessing}
                                >
                                    <Paperclip className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Send button */}
                            <button
                                onClick={onSend}
                                disabled={!canSend}
                                className={`p-1.5 rounded-md transition-all duration-200 ${!canSend
                                    ? 'opacity-0 pointer-events-none bg-gray-300'
                                    : 'opacity-100 bg-black dark:bg-white text-white dark:text-black hover:opacity-90 shadow-sm'
                                    }`}
                                title="Enviar mensagem"
                            >
                                {lifecycle === 'thinking' ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    )
})
